import { Check } from 'lucide-react';
import {
  ListBox as AriaListBox,
  ListBoxItem as AriaListBoxItem,
  ListBoxProps as AriaListBoxProps,
  Collection,
  Header,
  ListBoxItemProps,
  Section,
  SectionProps,
  composeRenderProps
} from 'react-aria-components';
import { tv } from 'tailwind-variants';
import { composeTailwindRenderProps, focusRing } from '~/library/utilities';

interface ListBoxProps<T> extends Omit<AriaListBoxProps<T>, 'layout' | 'orientation'> {}

export function ListBox<T extends object>(
  { children, ...props }: ListBoxProps<T>
) {
  return (
    <AriaListBox {...props} className={composeTailwindRenderProps(props.className, 'u-outline-0 u-p-1 u-border u-border-gray-300 dark:u-border-zinc-600 u-rounded-lg')}>
      {children}
    </AriaListBox>
  );
}

export const itemStyles = tv({
  extend: focusRing,
  base: 'group u-relative u-flex u-items-center u-gap-8 u-cursor-default u-select-none u-py-1.5 u-px-2.5',
  variants: {
    isSelected: {
      false: 'u-text-blue hover:u-bg-black/10',
      true: 'u-text-blue u-bg-black/10'
    },
    isDisabled: {
      true: 'text-slate-300 dark:text-zinc-600 forced-colors:text-[GrayText]'
    }
  }
});

export function ListBoxItem(props: ListBoxItemProps) {
  let textValue = props.textValue || (typeof props.children === 'string' ? props.children : undefined);
  return (
    <AriaListBoxItem {...props} textValue={textValue} className={itemStyles}>
      {composeRenderProps(props.children, children => <>
        {children}
        <div className="u-absolute u-left-4 u-right-4 u-bottom-0 u-h-px u-bg-white/20 forced-colors:u-bg-[HighlightText] u-hidden u-[.group[data-selected]:has(+[data-selected])_&]:block" />
      </>)}
    </AriaListBoxItem>
  );
}

export const dropdownItemStyles = tv({
  base: 'u-group u-flex u-items-center u-gap-4 u-cursor-default u-select-none u-py-2 u-pl-3 u-pr-1 u-outline u-outline-0',
  variants: {
    isDisabled: {
      false: 'u-text-blue',
      true: 'u-text-blue/20'
    },
    isFocused: {
      true: 'u-text-blue hover:u-bg-blue-10'
    }
  }
});

export function DropdownItem(props: ListBoxItemProps) {
  let textValue = props.textValue || (typeof props.children === 'string' ? props.children : undefined);
  return (
    <AriaListBoxItem {...props} textValue={textValue} className={dropdownItemStyles}>
      {composeRenderProps(props.children, (children, {isSelected}) => <>
        <span className="u-flex u-items-center u-flex-1 u-gap-2 u-text-[1.4rem] group-has-[button]:u-text-[1.6rem]">
          {children}
        </span>
        <span className="u-flex u-items-center u-w-6">
          {isSelected && <Check className="u-w-6 u-h-6 u-text-pink" />}
        </span>
      </>)}
    </AriaListBoxItem>
  );
}

export interface DropdownSectionProps<T> extends SectionProps<T> {
  title?: string
}

export function DropdownSection<T extends object>(props: DropdownSectionProps<T>) {
  return (
    <Section className="first:-mt-[5px] after:content-[''] after:block after:h-[5px]">
      <Header className="text-sm font-semibold text-gray-500 dark:text-zinc-300 px-4 py-1 truncate sticky -top-[5px] -mt-px -mx-1 z-10 bg-gray-100/60 dark:bg-zinc-700/60 backdrop-blur-md supports-[-moz-appearance:none]:bg-gray-100 border-y dark:border-y-zinc-700 [&+*]:mt-1">{props.title}</Header>
      <Collection items={props.items}>
        {props.children}
      </Collection>
    </Section>
  );
}
